/**
 * A request's page (`/feedback/:feedbackId`): its facts, then two cards. The
 * Request card holds it as filed (description and attachments); the Activity
 * card opens with the box to comment, then every comment and status change,
 * newest first (the API sends them oldest first; this page reverses them).
 *
 * The author cancels it while it is open; an admin changes its status, with
 * an optional note the author reads on the thread. What each person may do
 * comes from the server (`permissions`), not from a role check here.
 */

import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Ban, FileText, ListChecks, MessagesSquare } from 'lucide-react';

import { useFeedback } from '@/api/queries';
import type { FeedbackDetail } from '@/api/types';
import { Markdown } from '@/ui/Markdown';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';
import { formatDate, initialsOf } from '@/ui/format';

import { AttachmentList } from './Attachments';
import type { FeedbackLinkState } from './feedbackParams';
import { CancelDialog, StatusDialog } from './FeedbackStatusDialogs';
import { CommentBox, ThreadEntry, When, Who } from './FeedbackThread';
import { CategoryLabel, StatusPill } from './feedbackUi';

type Dialog = 'status' | 'cancel' | null;

function FactsCard({ item }: { item: FeedbackDetail }) {
  return (
    <div className="card idea-facts">
      <div className="idea-facts-main">
        <span className="idea-fact">
          <span className="label">Type</span>
          <CategoryLabel category={item.category} />
        </span>
        <span className="idea-fact">
          <span className="label">Status</span>
          <StatusPill status={item.status} />
        </span>
        <span className="idea-fact">
          <span className="label">Last activity</span>
          <When iso={item.last_activity_at} />
        </span>
        {item.closed_at && (
          <span className="idea-fact">
            <span className="label">Closed</span>
            {formatDate(item.closed_at)}
          </span>
        )}
      </div>
    </div>
  );
}

/** The request as filed: what it is about, apart from what happened since. */
function RequestCard({ item }: { item: FeedbackDetail }) {
  return (
    <section className="card feedback-thread-card" aria-labelledby="feedback-request-head">
      <h2 className="feedback-section-head" id="feedback-request-head">
        <FileText aria-hidden="true" /> Request
      </h2>
      <div className="thread-comment">
        <span className="avatar sm muted" aria-hidden="true">
          {initialsOf(item.author.display_name)}
        </span>
        <div className="thread-bubble">
          <div className="thread-meta">
            <Who person={item.author} requester />
            <span className="hint">
              · filed <When iso={item.created_at} inline />
            </span>
          </div>
          <Markdown>{item.description}</Markdown>
          <AttachmentList attachments={item.attachments} />
        </div>
      </div>
    </section>
  );
}

/** What happened since, newest first, under the box to add to it. */
function ActivityCard({ item }: { item: FeedbackDetail }) {
  const count = item.activity.length;
  return (
    <section className="card feedback-thread-card section-gap" aria-labelledby="feedback-activity-head">
      <h2 className="feedback-section-head" id="feedback-activity-head">
        <MessagesSquare aria-hidden="true" /> Activity
        {count > 0 && <span className="count">{count}</span>}
        <span className="hint">Newest first</span>
      </h2>
      <CommentBox item={item} />
      {count === 0 ? (
        <p className="hint thread-empty">No comments or status changes yet.</p>
      ) : (
        <ol className="feedback-thread" reversed>
          {[...item.activity].reverse().map((entry) => (
            <ThreadEntry key={entry.id} entry={entry} />
          ))}
        </ol>
      )}
    </section>
  );
}

function DetailView({
  item,
  back,
  dialog,
  setDialog,
}: {
  item: FeedbackDetail;
  back: { to: string; name: string };
  dialog: Dialog;
  setDialog: (dialog: Dialog) => void;
}) {
  const close = () => setDialog(null);
  return (
    <>
      <PageHead title={item.title} back={back} subtitle={`Filed by ${item.author.display_name} on ${formatDate(item.created_at)}`}>
        {item.permissions.can_cancel && (
          <button type="button" className="btn btn-secondary" onClick={() => setDialog('cancel')}>
            <Ban aria-hidden="true" /> Cancel request
          </button>
        )}
        {item.permissions.can_set_status && (
          <button type="button" className="btn btn-primary" onClick={() => setDialog('status')}>
            <ListChecks aria-hidden="true" /> Change status
          </button>
        )}
      </PageHead>

      <FactsCard item={item} />
      <RequestCard item={item} />
      <ActivityCard item={item} />

      {dialog === 'status' && <StatusDialog item={item} onClose={close} />}
      {dialog === 'cancel' && <CancelDialog item={item} onClose={close} />}
    </>
  );
}

export function FeedbackDetailPage() {
  const { feedbackId = '' } = useParams();
  const query = useFeedback(feedbackId);
  const location = useLocation();
  const [dialog, setDialog] = useState<Dialog>(null);

  const from = (location.state as FeedbackLinkState | null)?.from;
  const back = { to: from ?? '/feedback', name: 'Feedback' };
  const loadingHead = <PageHead title="Feedback request" back={back} />;

  if (query.isPending) return <>{loadingHead}<LoadingCard label="Loading the request" /></>;
  if (query.error) return <>{loadingHead}<LoadFailure error={query.error} onRetry={() => void query.refetch()} /></>;
  return <DetailView item={query.data} back={back} dialog={dialog} setDialog={setDialog} />;
}
